/**
 * FormSchemaOverrideDetailPage - FSO detail view (DRR-207)
 *
 * Shows:
 *   - Base form schema info with link back to FHIR entity
 *   - Override fields (hidden, readonly, custom)
 *   - Regulatory authority (edges to rulesets)
 *   - Form preview
 *   - Approval status and "Request Review" workflow
 *
 * Route: /configure/form-overrides/:subgraph/:entityType
 */

import { useMemo, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Breadcrumbs,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  ThemeIcon,
  Timeline,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAdjustments,
  IconAlertTriangle,
  IconArrowLeft,
  IconCheck,
  IconClock,
  IconDatabase,
  IconEye,
  IconEyeOff,
  IconGavel,
  IconLock,
  IconPlus,
  IconSend,
  IconShieldCheck,
  IconX,
} from '@tabler/icons-react';
import {
  useFormSchemaQueryQuery,
  useGetFormSchemaOverrideQuery,
  useCreateApprovalMutation,
  GqlApprovalType,
  GqlApprovalPriority,
  GqlApprovalStatus,
} from '@/generated/graphql';
import { useDevActorId } from '@/hooks/useDevActorId';
import { useTenantId } from '@/auth/AuthContext';

const SAFETY_COLORS: Record<string, string> = {
  CRITICAL: 'red',
  HIGH: 'orange',
  MEDIUM: 'yellow',
  LOW: 'green',
  NONE: 'gray',
};

const APPROVAL_STATUS_COLORS: Record<string, string> = {
  PENDING: 'blue',
  IN_REVIEW: 'cyan',
  APPROVED: 'green',
  REJECTED: 'red',
  ESCALATED: 'orange',
  EXPIRED: 'gray',
  WITHDRAWN: 'gray',
};

const APPROVAL_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending Review',
  IN_REVIEW: 'In Review',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  ESCALATED: 'Escalated',
  EXPIRED: 'Expired',
  WITHDRAWN: 'Withdrawn',
};

export function FormSchemaOverrideDetailPage() {
  const { subgraph, entityType } = useParams<{ subgraph: string; entityType: string }>();
  const tenantId = useTenantId();
  const actorId = useDevActorId();
  const [reviewModalOpened, { open: openReviewModal, close: closeReviewModal }] = useDisclosure(false);
  const [reviewNotes, setReviewNotes] = useState('');

  // Query base form schema
  const { data: baseData, loading: baseLoading, error: baseError } = useFormSchemaQueryQuery({
    variables: { subgraph: subgraph ?? '', entityType: entityType ?? '' },
    skip: !subgraph || !entityType,
  });

  // Query override
  const { data: overrideData, loading: overrideLoading } = useGetFormSchemaOverrideQuery({
    variables: { subgraph: subgraph ?? '', entityType: entityType ?? '' },
    skip: !subgraph || !entityType,
  });

  // Query approvals linked to this FSO
  // TODO: Add ListApprovals query with entity filter (backend resolver needed)
  const fsoId = `fso-${subgraph}-${entityType}`;
  const approvalsData = null; // Stub until ListApprovals query exists
  const approvalsLoading = false;
  const refetchApprovals = () => Promise.resolve();

  // Create approval mutation
  const [createApproval, { loading: createApprovalLoading }] = useCreateApprovalMutation();

  const baseSchema = baseData?.formSchema;
  const override = overrideData?.formSchemaOverride;
  const loading = baseLoading || overrideLoading;

  // Get latest approval for this FSO
  // TODO: Wire to ListApprovals query when backend resolver exists
  const latestApproval = null;

  // Determine FSO status based on approval
  const fsoStatus = useMemo(() => {
    if (!override) return 'NO_OVERRIDE';
    if (!latestApproval) return 'DRAFT';
    return latestApproval.status;
  }, [override, latestApproval]);

  // Handle request review submission
  const handleRequestReview = useCallback(async () => {
    if (!tenantId || !actorId) {
      notifications.show({
        title: 'Error',
        message: 'Missing tenant or actor context',
        color: 'red',
      });
      return;
    }

    try {
      await createApproval({
        variables: {
          input: {
            approvalName: `FSO Review: ${subgraph}:${entityType}`,
            approvalType: GqlApprovalType.ConfigChange,
            approverId: actorId,
            approverRole: 'architect',
            policyId: 'policy-fso-review',
            workflowInstanceId: `fso-review-${Date.now()}`,
            entityType: 'FormSchemaOverride',
            entityId: fsoId,
            priority: GqlApprovalPriority.Medium,
            metadata: JSON.stringify({
              subgraph,
              entityType,
              notes: reviewNotes,
              requestedBy: actorId,
              requestedAt: new Date().toISOString(),
            }),
            tags: ['fso', 'form-schema', subgraph ?? ''],
          },
        },
      });

      notifications.show({
        title: 'Review Requested',
        message: 'FSO review request has been submitted',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      closeReviewModal();
      setReviewNotes('');
      refetchApprovals();
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create approval request',
        color: 'red',
      });
    }
  }, [tenantId, actorId, subgraph, entityType, fsoId, reviewNotes, createApproval, closeReviewModal, refetchApprovals]);

  // Mock regulatory edges (would come from backend in production)
  const regulatoryEdges = useMemo(() => {
    if (subgraph !== 'fhir') return [];
    // Mock data based on entity type
    const edgeMap: Record<string, Array<{ rulesetId: string; rulesetName: string; provisions: string[] }>> = {
      FhirPatient: [
        { rulesetId: 'rs-4-1-001', rulesetName: 'MN 144G', provisions: ['144G.61', '144G.63'] },
      ],
      FhirPractitioner: [
        { rulesetId: 'rs-4-1-001', rulesetName: 'MN 144G', provisions: ['144G.30', '144G.70', '144G.71'] },
      ],
      FhirCarePlan: [
        { rulesetId: 'rs-4-1-001', rulesetName: 'MN 144G', provisions: ['144G.65', '144G.66'] },
      ],
      FhirObservation: [
        { rulesetId: 'rs-4-1-001', rulesetName: 'MN 144G', provisions: ['144G.63'] },
      ],
    };
    return edgeMap[entityType ?? ''] ?? [];
  }, [subgraph, entityType]);

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      </Container>
    );
  }

  if (baseError || !baseSchema) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Schema Not Found">
          {baseError?.message ?? `No form schema found for ${subgraph}:${entityType}`}
        </Alert>
      </Container>
    );
  }

  const hasOverride = !!override;
  const hiddenFields = override?.hiddenFields ?? [];
  const readonlyFields = override?.readonlyFields ?? [];
  const customFields = override?.customFields ?? [];
  // labelOverrides is stored as JSON string, parse it
  const labelOverrides: Array<{ fieldName: string; label: string }> = useMemo(() => {
    if (!override?.labelOverrides) return [];
    try {
      const parsed = JSON.parse(override.labelOverrides);
      if (Array.isArray(parsed)) return parsed;
      // Handle object format { fieldName: label }
      return Object.entries(parsed).map(([fieldName, label]) => ({ fieldName, label: String(label) }));
    } catch {
      return [];
    }
  }, [override?.labelOverrides]);

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs>
          <Anchor component={Link} to="/configure/form-overrides" size="sm">
            Form Overrides
          </Anchor>
          <Text size="sm" c="dimmed">{baseSchema.displayName}</Text>
        </Breadcrumbs>

        {/* Header */}
        <Paper p="lg" withBorder>
          <Group justify="space-between" align="flex-start">
            <Group gap="sm">
              <ThemeIcon size="lg" variant="light" color="indigo">
                <IconAdjustments size={20} />
              </ThemeIcon>
              <div>
                <Title order={3}>{baseSchema.displayName}</Title>
                <Text size="sm" c="dimmed" ff="monospace">
                  {subgraph}:{entityType}
                </Text>
              </div>
            </Group>
            <Group gap="xs">
              <Badge
                color={SAFETY_COLORS[baseSchema.safetyLevel] ?? 'gray'}
                variant="filled"
              >
                {baseSchema.safetyLevel}
              </Badge>
              <Badge variant="outline" color="gray">v{baseSchema.version}</Badge>
              {hasOverride && (
                <Badge color="indigo" variant="light">Has Override</Badge>
              )}
              {/* FSO Approval Status Badge */}
              {fsoStatus !== 'NO_OVERRIDE' && (
                <Badge
                  color={fsoStatus === 'DRAFT' ? 'yellow' : APPROVAL_STATUS_COLORS[fsoStatus] ?? 'gray'}
                  variant="filled"
                  leftSection={
                    fsoStatus === 'APPROVED' ? <IconShieldCheck size={12} /> :
                    fsoStatus === 'PENDING' || fsoStatus === 'IN_REVIEW' ? <IconClock size={12} /> :
                    fsoStatus === 'REJECTED' ? <IconX size={12} /> : null
                  }
                >
                  {fsoStatus === 'DRAFT' ? 'Draft' : APPROVAL_STATUS_LABELS[fsoStatus] ?? fsoStatus}
                </Badge>
              )}
            </Group>
          </Group>

          {baseSchema.description && (
            <Text size="sm" c="dimmed" mt="md">{baseSchema.description}</Text>
          )}

          {/* Action Buttons */}
          {hasOverride && (
            <Group mt="md" gap="sm">
              {(fsoStatus === 'DRAFT' || fsoStatus === 'REJECTED') && (
                <Button
                  leftSection={<IconSend size={16} />}
                  variant="filled"
                  color="blue"
                  onClick={openReviewModal}
                  loading={createApprovalLoading}
                >
                  Request Review
                </Button>
              )}
              {fsoStatus === 'APPROVED' && (
                <Alert icon={<IconShieldCheck size={16} />} color="green" p="xs">
                  This override has been approved and is active
                </Alert>
              )}
              {(fsoStatus === 'PENDING' || fsoStatus === 'IN_REVIEW') && (
                <Alert icon={<IconClock size={16} />} color="blue" p="xs">
                  Review in progress — check the Approval Inbox for updates
                </Alert>
              )}
            </Group>
          )}

          {/* Link back to FHIR entity */}
          {subgraph === 'fhir' && (
            <Group mt="md">
              <Anchor
                component={Link}
                to={`/fhir?entity=${entityType}`}
                size="sm"
                c="blue"
              >
                <Group gap={4}>
                  <IconDatabase size={14} />
                  View {baseSchema.displayName} data in Healthcare (FHIR)
                </Group>
              </Anchor>
            </Group>
          )}
        </Paper>

        {/* Regulatory Authority */}
        {regulatoryEdges.length > 0 && (
          <Paper p="lg" withBorder>
            <Group gap="sm" mb="md">
              <ThemeIcon variant="light" color="blue">
                <IconGavel size={18} />
              </ThemeIcon>
              <Title order={4}>Regulatory Authority</Title>
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {regulatoryEdges.map((edge) => (
                <Card key={edge.rulesetId} withBorder p="sm">
                  <Group justify="space-between" mb="xs">
                    <Text fw={600}>{edge.rulesetName}</Text>
                    <Badge variant="outline" color="gray" size="xs">{edge.rulesetId}</Badge>
                  </Group>
                  <Group gap={4}>
                    {edge.provisions.map((p) => (
                      <Badge key={p} variant="light" color="blue" size="xs">{p}</Badge>
                    ))}
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          </Paper>
        )}

        {/* Approval History */}
        {hasOverride && (approvalsData?.approvals?.length ?? 0) > 0 && (
          <Paper p="lg" withBorder>
            <Group gap="sm" mb="md">
              <ThemeIcon variant="light" color="violet">
                <IconShieldCheck size={18} />
              </ThemeIcon>
              <Title order={4}>Approval History</Title>
            </Group>
            <Timeline active={0} bulletSize={24} lineWidth={2}>
              {[...(approvalsData?.approvals ?? [])]
                .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                .slice(0, 5)
                .map((approval, index) => (
                  <Timeline.Item
                    key={approval.id}
                    bullet={
                      approval.status === 'APPROVED' ? <IconCheck size={12} /> :
                      approval.status === 'REJECTED' ? <IconX size={12} /> :
                      <IconClock size={12} />
                    }
                    color={APPROVAL_STATUS_COLORS[approval.status] ?? 'gray'}
                    title={
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          {APPROVAL_STATUS_LABELS[approval.status] ?? approval.status}
                        </Text>
                        <Badge size="xs" variant="light" color={APPROVAL_STATUS_COLORS[approval.status] ?? 'gray'}>
                          {approval.status}
                        </Badge>
                      </Group>
                    }
                  >
                    <Text size="xs" c="dimmed">
                      {new Date(approval.createdAt).toLocaleString()}
                    </Text>
                    {approval.metadata && (
                      <Text size="xs" c="dimmed" mt={4}>
                        {(() => {
                          try {
                            const meta = JSON.parse(approval.metadata);
                            return meta.notes || null;
                          } catch {
                            return null;
                          }
                        })()}
                      </Text>
                    )}
                  </Timeline.Item>
                ))}
            </Timeline>
            {(approvalsData?.approvals?.length ?? 0) > 5 && (
              <Text size="xs" c="dimmed" mt="sm">
                Showing 5 of {approvalsData?.approvals?.length} approval records
              </Text>
            )}
          </Paper>
        )}

        {/* Override Details */}
        {hasOverride ? (
          <Paper p="lg" withBorder>
            <Title order={4} mb="md">Override Configuration</Title>

            {/* Hidden Fields */}
            {hiddenFields.length > 0 && (
              <>
                <Group gap="xs" mb="sm">
                  <ThemeIcon size="sm" variant="light" color="red">
                    <IconEyeOff size={14} />
                  </ThemeIcon>
                  <Text fw={600} size="sm">Hidden Fields ({hiddenFields.length})</Text>
                </Group>
                <Group gap="xs" mb="md">
                  {hiddenFields.map((f) => (
                    <Badge key={f} variant="light" color="red" size="sm">{f}</Badge>
                  ))}
                </Group>
                <Divider my="sm" />
              </>
            )}

            {/* Readonly Fields */}
            {readonlyFields.length > 0 && (
              <>
                <Group gap="xs" mb="sm">
                  <ThemeIcon size="sm" variant="light" color="orange">
                    <IconLock size={14} />
                  </ThemeIcon>
                  <Text fw={600} size="sm">Read-Only Fields ({readonlyFields.length})</Text>
                </Group>
                <Group gap="xs" mb="md">
                  {readonlyFields.map((f) => (
                    <Badge key={f} variant="light" color="orange" size="sm">{f}</Badge>
                  ))}
                </Group>
                <Divider my="sm" />
              </>
            )}

            {/* Label Overrides */}
            {labelOverrides.length > 0 && (
              <>
                <Group gap="xs" mb="sm">
                  <ThemeIcon size="sm" variant="light" color="cyan">
                    <IconEye size={14} />
                  </ThemeIcon>
                  <Text fw={600} size="sm">Label Overrides ({labelOverrides.length})</Text>
                </Group>
                <Table striped mb="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Field</Table.Th>
                      <Table.Th>Custom Label</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {labelOverrides.map((lo) => (
                      <Table.Tr key={lo.fieldName}>
                        <Table.Td><Text ff="monospace" size="sm">{lo.fieldName}</Text></Table.Td>
                        <Table.Td>{lo.label}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                <Divider my="sm" />
              </>
            )}

            {/* Custom Fields */}
            {customFields.length > 0 && (
              <>
                <Group gap="xs" mb="sm">
                  <ThemeIcon size="sm" variant="light" color="green">
                    <IconPlus size={14} />
                  </ThemeIcon>
                  <Text fw={600} size="sm">Custom Fields ({customFields.length})</Text>
                </Group>
                <Table striped>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Field Name</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>Required</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {customFields.map((cf) => (
                      <Table.Tr key={cf.name}>
                        <Table.Td>
                          <Text ff="monospace" size="sm">{cf.name}</Text>
                          {cf.label && <Text size="xs" c="dimmed">{cf.label}</Text>}
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="outline" size="xs">{cf.fieldType}</Badge>
                        </Table.Td>
                        <Table.Td>
                          {cf.required ? (
                            <IconCheck size={14} color="green" />
                          ) : (
                            <Text size="xs" c="dimmed">-</Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}

            {/* No changes */}
            {hiddenFields.length === 0 && readonlyFields.length === 0 &&
             labelOverrides.length === 0 && customFields.length === 0 && (
              <Text c="dimmed" size="sm">
                Override exists but has no field modifications.
              </Text>
            )}
          </Paper>
        ) : (
          <Alert icon={<IconAdjustments size={16} />} color="gray" title="No Override Configured">
            This form schema uses the base definition without tenant-specific customization.
            Use the saveFormSchemaOverride mutation to add jurisdiction-specific fields.
          </Alert>
        )}

        {/* Form Preview: Shows effective form with overrides applied */}
        {baseSchema.fields && baseSchema.fields.length > 0 && (
          <Paper p="lg" withBorder>
            <Group gap="sm" mb="md">
              <ThemeIcon variant="light" color="violet">
                <IconEye size={18} />
              </ThemeIcon>
              <Title order={4}>Form Preview (With Overrides)</Title>
            </Group>
            <Text size="sm" c="dimmed" mb="md">
              This preview shows how the form will render with all overrides applied.
              Hidden fields are removed, readonly fields are marked, and custom labels are shown.
            </Text>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {/* Filter out hidden fields, apply label overrides */}
              {baseSchema.fields
                .filter((field) => !hiddenFields.includes(field.name))
                .slice(0, 10)
                .map((field) => {
                  const labelOverride = labelOverrides.find((lo) => lo.fieldName === field.name);
                  const isReadonly = readonlyFields.includes(field.name);
                  return (
                    <Card key={field.name} withBorder p="sm" radius="sm">
                      <Group justify="space-between" mb="xs">
                        <Group gap="xs">
                          <Text fw={500} size="sm">
                            {labelOverride?.label ?? field.label ?? field.name}
                          </Text>
                          {labelOverride && (
                            <Badge size="xs" variant="light" color="cyan">
                              custom label
                            </Badge>
                          )}
                        </Group>
                        <Group gap={4}>
                          {field.required && (
                            <Badge size="xs" color="red" variant="light">
                              required
                            </Badge>
                          )}
                          {isReadonly && (
                            <Badge size="xs" color="orange" variant="light">
                              readonly
                            </Badge>
                          )}
                        </Group>
                      </Group>
                      <Text size="xs" c="dimmed" ff="monospace">
                        {field.fieldType}
                      </Text>
                    </Card>
                  );
                })}
              {/* Show custom fields added by override */}
              {customFields.map((cf) => (
                <Card key={cf.name} withBorder p="sm" radius="sm" bg="green.0">
                  <Group justify="space-between" mb="xs">
                    <Group gap="xs">
                      <Text fw={500} size="sm">
                        {cf.label ?? cf.name}
                      </Text>
                      <Badge size="xs" variant="filled" color="green">
                        custom field
                      </Badge>
                    </Group>
                    {cf.required && (
                      <Badge size="xs" color="red" variant="light">
                        required
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed" ff="monospace">
                    {cf.fieldType}
                  </Text>
                </Card>
              ))}
            </SimpleGrid>
            {baseSchema.fields.filter((f) => !hiddenFields.includes(f.name)).length > 10 && (
              <Text size="xs" c="dimmed" mt="sm">
                Showing 10 of {baseSchema.fields.filter((f) => !hiddenFields.includes(f.name)).length} visible fields
              </Text>
            )}
          </Paper>
        )}

        {/* Base Fields */}
        <Paper p="lg" withBorder>
          <Title order={4} mb="md">Base Form Fields</Title>
          {baseSchema.fields && baseSchema.fields.length > 0 ? (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Field</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Required</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {baseSchema.fields.slice(0, 15).map((field) => (
                  <Table.Tr key={field.name}>
                    <Table.Td>
                      <Text ff="monospace" size="sm">{field.name}</Text>
                      {field.label && <Text size="xs" c="dimmed">{field.label}</Text>}
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="outline" size="xs">{field.fieldType}</Badge>
                    </Table.Td>
                    <Table.Td>
                      {field.required ? (
                        <IconCheck size={14} color="green" />
                      ) : (
                        <Text size="xs" c="dimmed">-</Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            <Text c="dimmed" size="sm">No fields defined in base schema.</Text>
          )}
          {baseSchema.fields && baseSchema.fields.length > 15 && (
            <Text size="xs" c="dimmed" mt="sm">
              Showing 15 of {baseSchema.fields.length} fields
            </Text>
          )}
        </Paper>

        {/* Back link */}
        <Anchor component={Link} to="/configure/form-overrides" size="sm">
          <Group gap={4}>
            <IconArrowLeft size={14} />
            Back to Form Overrides
          </Group>
        </Anchor>
      </Stack>

      {/* Request Review Modal */}
      <Modal
        opened={reviewModalOpened}
        onClose={closeReviewModal}
        title={
          <Group gap="sm">
            <ThemeIcon variant="light" color="blue">
              <IconSend size={18} />
            </ThemeIcon>
            <Text fw={600}>Request FSO Review</Text>
          </Group>
        }
        size="md"
      >
        <Stack gap="md">
          <Alert icon={<IconShieldCheck size={16} />} color="blue">
            This will create an approval request for the Form Schema Override.
            An architect will review and approve/reject the changes.
          </Alert>

          <Card withBorder p="sm">
            <Group justify="space-between">
              <Text size="sm" fw={500}>Override</Text>
              <Text size="sm" ff="monospace" c="dimmed">{subgraph}:{entityType}</Text>
            </Group>
            <Group justify="space-between" mt="xs">
              <Text size="sm" fw={500}>Custom Fields</Text>
              <Badge size="sm" variant="light">{customFields.length}</Badge>
            </Group>
            <Group justify="space-between" mt="xs">
              <Text size="sm" fw={500}>Hidden Fields</Text>
              <Badge size="sm" variant="light">{hiddenFields.length}</Badge>
            </Group>
          </Card>

          <Textarea
            label="Review Notes"
            placeholder="Describe the changes and why they're needed..."
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.currentTarget.value)}
            minRows={3}
          />

          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={closeReviewModal}>
              Cancel
            </Button>
            <Button
              leftSection={<IconSend size={16} />}
              onClick={handleRequestReview}
              loading={createApprovalLoading}
            >
              Submit for Review
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default FormSchemaOverrideDetailPage;
